import type { Store } from "@reduxjs/toolkit";
import type { CanvasState } from "@v1/components/canvas/canvas-types";
import type { AIState } from "@v1/reducers/ai-reducer";
import type { CandidateState } from "@v1/reducers/candidate-reducer";
import type { CommerceState } from "@v1/reducers/commerce-reducer";
import type { ComponentTemplatesState } from "@v1/reducers/component-templates-reducer";
import type { DragAndDropState } from "@v1/reducers/drag-and-drop-reducer";
import type { EditorMediaState } from "@v1/reducers/editor-media-reducer";
import type { FigmaToReploState } from "@v1/reducers/figma-to-replo-reducer";
import type { PaintState } from "@v1/reducers/paint-reducer";
import type { TreeState } from "@v1/reducers/tree-reducer";
import type { UIState } from "@v1/reducers/ui-reducer";
import type { CoreState } from "@v1/types/core-state";
import type * as React from "react";
import type { TypedUseSelectorHook } from "react-redux";
import type { Action } from "reduce-reducers";
import type { ProductRef } from "schemas/product";

import "@v1/middleware/modal-bridge";

import { combineReducers, configureStore } from "@reduxjs/toolkit";
import canvasReducer from "@v1/components/canvas/canvas-reducer";
import errorMiddleware from "@v1/middleware/error";
import listenerMiddleware from "@v1/middleware/listener";
import loggingMiddleware from "@v1/middleware/logging";
import redirectMiddleware from "@v1/middleware/redirect";
import { sequenceActionMiddleware } from "@v1/middleware/sequence-action";
import { setDraftElementMiddleware } from "@v1/middleware/set-draft-element";
import updateCheckingMiddleware from "@v1/middleware/update-checking";
import aiReducer, { aiApi } from "@v1/reducers/ai-reducer";
import candidateReducer from "@v1/reducers/candidate-reducer";
import { reducer as commerceReducer } from "@v1/reducers/commerce-reducer";
import componentTemplatesReducer from "@v1/reducers/component-templates-reducer";
import { coreReducer } from "@v1/reducers/core-reducer";
import dragAndDropReducer from "@v1/reducers/drag-and-drop-reducer";
import editorMediaReducer from "@v1/reducers/editor-media-reducer";
import errorReducer from "@v1/reducers/error-reducer";
import figmaToReploReducer from "@v1/reducers/figma-to-replo-reducer";
import paintReducer from "@v1/reducers/paint-reducer";
import templateReducer from "@v1/reducers/template-reducer";
import treeReducer from "@v1/reducers/tree-reducer";
import uiReducer from "@v1/reducers/ui-reducer";
// eslint-disable-next-line no-restricted-imports
import { shallowEqual, useDispatch, useSelector, useStore } from "react-redux";
import { canUseDOM } from "replo-utils/dom/misc";

const rootReducer = combineReducers({
  core: coreReducer,
  commerce: commerceReducer,
  dragAndDrop: dragAndDropReducer,
  canvas: canvasReducer,
  paint: paintReducer,
  candidate: candidateReducer,
  ui: uiReducer,
  error: errorReducer,
  tree: treeReducer,
  template: templateReducer,
  componentTemplates: componentTemplatesReducer,
  editorMedia: editorMediaReducer,
  figmaToReplo: figmaToReploReducer,
  ai: aiReducer,
  [aiApi.reducerPath]: aiApi.reducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware({
      serializableCheck: isUsingDevMiddleware(),
      immutableCheck: isUsingDevMiddleware(),
    })
      .prepend(
        setDraftElementMiddleware,
        sequenceActionMiddleware,
        errorMiddleware,
        loggingMiddleware,
        redirectMiddleware,
        listenerMiddleware.middleware,
      )
      .concat([updateCheckingMiddleware, aiApi.middleware]);
  },
  devTools:
    process.env.NODE_ENV !== "production"
      ? {
          stateSanitizer: (state) => {
            return {
              ...state,
              // Comment
              // Note (Noah, 2022-02-21): The canvas state includes a very large srcdoc string,
              // which redux devtools copies for every action/new state. This results in a huge
              // memory blowup (every single new state has ~500kb of copied string!) so we
              // sanitize away the large data to make devtools work. See:
              // https://github.com/zalmoxisus/redux-devtools-extension/issues/455
              canvas: {
                // @ts-ignore
                ...state.canvas,
                srcDoc:
                  "[Replo Local Dev]: Canvas srcDoc omitted for Redux devtools performance.",
              },
            };
          },
        }
      : false,
});

export type EditorRootState = {
  core: CoreState;
  commerce: CommerceState;
  dragAndDrop: DragAndDropState;
  canvas: CanvasState;
  paint: PaintState;
  candidate: CandidateState;
  ui: UIState;

  error: {};
  tree: TreeState;
  template: { templateEditorProduct: ProductRef | null };
  componentTemplates: ComponentTemplatesState;
  editorMedia: EditorMediaState;
  figmaToReplo: FigmaToReploState;
  ai: AIState;
  [aiApi.reducerPath]: any;
};

export type EditorDispatch = typeof store.dispatch & React.Dispatch<Action[]>;
// NOTE (Patrick, 2026-02-26, EDITOR-FRONTEND-7E9): react-redux 9.x uses
// useSyncExternalStore, which compares selector results with ===. Selectors
// returning new object/array references (e.g. via .map(), .filter(), or object
// spreads) are always "different", triggering continuous synchronous re-renders.
// Defaulting to shallowEqual is a pragmatic hotfix. The proper long-term fix is
// to wrap any selector returning objects/arrays in createSelector for memoization.
export const useEditorSelector: TypedUseSelectorHook<EditorRootState> = (
  selector,
  equalityFn = shallowEqual,
) => useSelector(selector, equalityFn);
export const useEditorDispatch = () => useDispatch<EditorDispatch>();
export const useEditorStore = () => useStore<EditorRootState, Action>();
export type EditorStore = Store<EditorRootState, Action>;

function isUsingDevMiddleware() {
  if (!canUseDOM) {
    return false;
  }
  return (
    new URLSearchParams(window.location.search).get("dev-middleware") === "true"
  );
}
